import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  ScrollView,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { AntDesign, MaterialIcons, Feather } from '@expo/vector-icons';
import Axios from 'axios';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { USER_INFO_SUCCESS_FAIL } from '../../constants';
import { getAuthHeaders, getBaseUrl } from '../../helpers/rulesApiHelpers';
import { MAX_POLL_ATTEMPTS, POLL_INTERVAL_MS, DRY_RUN_THRESHOLD } from '../../helpers/rulesConfig';
import s from '../../style/reRunRulesModal';
import OrderChangeCard from './OrderChangeCard';

// ─── ReRunRulesModal ──────────────────────────────────────────────────────────
//
// Phase 7-FE: Manual re-run preview + apply modal.
// Opens with a spinner, fires dry-run POST inside useEffect, then renders preview.
// Apply sends POST with dry_run: false + preview_token.
// Async path (202) polls job status every 2 seconds.
//
// Props:
//   visible       - boolean, controls modal visibility
//   onClose       - function, called to close the modal
//   orderIds      - number[], selected order IDs
//   tenantId      - string, Apartment schema name (for display/logging)
//   onRerunFailed - function(failedOrderIds[]), optional callback for "Re-run failed orders"
//   alertBox      - function(message), shows a toast/alert

const ReRunRulesModal = ({
  visible,
  onClose,
  orderIds,
  tenantId,
  onRerunFailed,
  alertBox,
  skipDryRun = false,
}) => {
  // ─── State ──────────────────────────────────────────────────────────────────
  const [previewData, setPreviewData] = useState(null);
  const [previewToken, setPreviewToken] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [lockedOrderIds, setLockedOrderIds] = useState([]);
  const [polling, setPolling] = useState(false);
  const [pollUrl, setPollUrl] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [lastAction, setLastAction] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [unchangedExpanded, setUnchangedExpanded] = useState(false);
  const [dryRunSkipped, setDryRunSkipped] = useState(false);

  const dispatch = useDispatch();

  // Ref for cleanup
  const pollIntervalRef = useRef(null);

  const windowWidth = Dimensions.get('window').width;

  // ─── Reset state when modal visibility changes ──────────────────────────────
  useEffect(() => {
    if (visible && orderIds?.length > 0) {
      // Reset all state for fresh modal open
      setPreviewData(null);
      setPreviewToken(null);
      setApplyResult(null);
      setApplying(false);
      setError(null);
      setLockedOrderIds([]);
      setPolling(false);
      setPollUrl(null);
      setUnchangedExpanded(false);

      if (skipDryRun) {
        // 101-200 orders: skip dry-run, go straight to apply
        setDryRunSkipped(true);
        setLoadingPreview(false);
        setLastAction('apply');
      } else {
        setDryRunSkipped(false);
        setJobId(null);
        setLastAction('dry_run');
        setLoadingPreview(true);
        fireDryRun();
      }
    }

    // Stop polling when modal closes (visible becomes false)
    if (!visible && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      setPolling(false);
    }
  }, [visible]);

  // ─── Cleanup polling on unmount or modal close ──────────────────────────────
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // ─── Polling effect ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!polling || !pollUrl) return;

    let pollCount = 0;

    const interval = setInterval(async () => {
      pollCount++;
      if (pollCount >= MAX_POLL_ATTEMPTS) {
        clearInterval(interval);
        pollIntervalRef.current = null;
        setError('Polling timed out. Please check the order list for results.');
        setPolling(false);
        return;
      }
      try {
        const url = await getBaseUrl();
        const headers = await getAuthHeaders();
        const res = await Axios.get(`${url}${pollUrl}`, { headers });

        if (res.data.status === 'completed') {
          clearInterval(interval);
          pollIntervalRef.current = null;
          setPolling(false);
          handleApplySuccess(res.data);
        } else if (res.data.status === 'failed') {
          clearInterval(interval);
          pollIntervalRef.current = null;
          setPolling(false);
          setError(res.data.error || 'Job failed');
        }
        // 'queued' or 'processing' → continue polling
      } catch (err) {
        // Network error — continue polling, don't abort (GAP-2 fix)
        console.warn('[ReRunRulesModal] Poll error, retrying...', err.message);
      }
    }, POLL_INTERVAL_MS);

    pollIntervalRef.current = interval;

    return () => {
      clearInterval(interval);
      pollIntervalRef.current = null;
    };
  }, [polling, pollUrl]);

  // ─── Dry-run POST ──────────────────────────────────────────────────────────

  const fireDryRun = async () => {
    try {
      setLoadingPreview(true);
      setError(null);
      setLastAction('dry_run');

      const url = await getBaseUrl();
      const headers = await getAuthHeaders();

      const res = await Axios.post(
        `${url}/api/v1/rules_engine/evaluate_rules.json`,
        { order_ids: orderIds, dry_run: true },
        { headers }
      );

      setPreviewData(res.data);
      setPreviewToken(res.data.preview_token);
      setLoadingPreview(false);
    } catch (err) {
      setLoadingPreview(false);
      handleApiError(err);
    }
  };

  // ─── Apply POST ─────────────────────────────────────────────────────────────

  const handleApply = async () => {
    try {
      setApplying(true);
      setError(null);
      setLastAction('apply');

      const url = await getBaseUrl();
      const headers = await getAuthHeaders();

      const res = await Axios.post(
        `${url}/api/v1/rules_engine/evaluate_rules.json`,
        {
          order_ids: orderIds,
          dry_run: false,
          preview_token: previewToken,
        },
        { headers, validateStatus: (status) => status < 500 || status === 503 }
      );

      const status = res.status;

      if (status === 200) {
        handleApplySuccess(res.data);
      } else if (status === 202) {
        // Async path — start polling
        setJobId(res.data.job_id || null);
        setPollUrl(res.data.poll_url);
        setPolling(true);
        setApplying(false);
      } else if (status === 207) {
        setApplyResult(res.data);
        setApplying(false);
      } else if (status === 409) {
        setError(getConflictMessage(res.data));
        setLockedOrderIds(res.data?.locked_order_ids || []);
        setApplying(false);
      } else if (status === 400) {
        setError('Invalid request. Please try again.');
        setApplying(false);
      } else if (status === 401) {
        dispatch({ type: USER_INFO_SUCCESS_FAIL, payload: 401 });
        onClose();
        return;
      } else if (status === 403) {
        setError("You don't have permission to re-run rules.");
        setApplying(false);
      } else if (status === 422) {
        setError(res.data?.error || 'Validation error');
        setApplying(false);
      } else if (status === 503) {
        setError('Rules engine is temporarily unavailable. Please try again later.');
        setApplying(false);
      } else {
        setError('Something went wrong. Please try again.');
        setApplying(false);
      }
    } catch (err) {
      setApplying(false);
      handleApiError(err);
    }
  };

  // ─── Success handler ────────────────────────────────────────────────────────

  const handleApplySuccess = (data) => {
    // Check for partial failure (207-like response from async path)
    if (data.failed_orders && data.failed_orders.length > 0) {
      setApplyResult(data);
      setApplying(false);
      return;
    }

    // Full success — close modal and show toast
    const count = data.succeeded_orders?.length || orderIds.length;
    onClose();
    if (alertBox) {
      alertBox(`Rules applied to ${count} orders`);
    }
  };

  // ─── Error handler ──────────────────────────────────────────────────────────

  const handleApiError = (err) => {
    if (err.response) {
      const status = err.response.status;
      const data = err.response.data;

      if (status === 409) {
        setError(getConflictMessage(data));
        setLockedOrderIds(data?.locked_order_ids || []);
      } else if (status === 401) {
        dispatch({ type: USER_INFO_SUCCESS_FAIL, payload: 401 });
        onClose();
        return;
      } else if (status === 403) {
        setError("You don't have permission to re-run rules.");
      } else if (status === 422) {
        setError(data?.error || 'Validation error');
      } else if (status === 503) {
        setError('Rules engine is temporarily unavailable. Please try again later.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } else {
      setError('Network error. Please check your connection.');
    }
  };

  const getConflictMessage = (data) => {
    if (data?.code === 'order_locked') {
      return 'One or more orders are locked by another operation in progress. Please retry in a moment.';
    }
    return 'Orders are currently being processed by an active import. Please retry.';
  };

  // ─── Retry handler ─────────────────────────────────────────────────────────

  const handleRetry = () => {
    setError(null);
    setLockedOrderIds([]);
    if (lastAction === 'dry_run') {
      setLoadingPreview(true);
      fireDryRun();
    } else {
      handleApply();
    }
  };

  // ─── Re-run failed orders ──────────────────────────────────────────────────

  const handleRerunFailed = () => {
    if (applyResult?.failed_orders && onRerunFailed) {
      const failedIds = applyResult.failed_orders.map((f) => f.order_id);
      onClose();
      onRerunFailed(failedIds);
    }
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const renderLoading = (message) => (
    <View style={s.loadingContainer}>
      <ActivityIndicator size="large" color="#1CBB77" />
      <Text style={s.loadingText}>{message}</Text>
    </View>
  );

  const renderError = () => (
    <View style={s.errorContainer}>
      <MaterialIcons name="error-outline" size={32} color="#e74c3c" />
      <Text style={s.errorText}>{error}</Text>
      {lockedOrderIds.length > 0 && (
        <View style={s.lockedList}>
          <Text style={s.lockedLabel}>Locked orders:</Text>
          <Text style={s.lockedIds}>
            {lockedOrderIds.map((id) => `#${id}`).join(', ')}
          </Text>
        </View>
      )}
      <View style={s.errorButtons}>
        <TouchableOpacity onPress={handleRetry} style={s.retryButton}>
          <Text style={s.retryButtonText}>Retry</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onClose} style={s.closeButton}>
          <Text style={s.closeButtonText}>Close</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderOrderCard = (result) => (
    <OrderChangeCard key={result.order_id} result={result} />
  );

  const renderPreview = () => {
    if (!previewData) return null;

    const { summary, results } = previewData;
    const ordersWithChanges = results?.filter((r) => r.has_changes) || [];
    const ordersUnchanged = results?.filter((r) => !r.has_changes) || [];

    return (
      <View style={s.previewContainer}>
        {/* Summary bar */}
        <View style={s.summaryBar}>
          <Text style={s.summaryText}>
            {summary?.orders_with_changes || 0} orders will be modified, {summary?.orders_unchanged || 0} unchanged
          </Text>
        </View>

        {/* Order cards with changes */}
        <ScrollView style={s.scrollArea}>
          {ordersWithChanges.map(renderOrderCard)}

          {/* Unchanged orders — collapsed/expandable */}
          {ordersUnchanged.length > 0 && (
            <TouchableOpacity
              onPress={() => setUnchangedExpanded(!unchangedExpanded)}
              style={s.unchangedHeader}
            >
              <Text style={s.unchangedText}>
                {ordersUnchanged.length} orders: no changes
              </Text>
              <Feather
                name={unchangedExpanded ? 'chevron-up' : 'chevron-down'}
                size={18}
                color="#E0E7EF"
              />
            </TouchableOpacity>
          )}
          {unchangedExpanded &&
            ordersUnchanged.map((r) => (
              <View key={r.order_id} style={s.unchangedCard}>
                <Text style={s.unchangedOrderText}>
                  {r.order_number || `Order #${r.order_id}`}
                </Text>
              </View>
            ))}
        </ScrollView>
      </View>
    );
  };

  const renderApplyResult = () => {
    if (!applyResult) return null;

    const succeeded = applyResult.succeeded_orders || [];
    const failed = applyResult.failed_orders || [];

    return (
      <View style={s.resultContainer}>
        <ScrollView style={s.scrollArea}>
          {/* Succeeded orders */}
          {succeeded.map((orderId) => (
            <View key={orderId} style={s.resultRow}>
              <AntDesign name="checkcircle" size={18} color="#1CBB77" />
              <Text style={s.resultText}>Order #{orderId}</Text>
            </View>
          ))}

          {/* Failed orders */}
          {failed.map((item) => (
            <View key={item.order_id} style={s.resultRow}>
              <AntDesign name="warning" size={18} color="#f39c12" />
              <Text style={s.resultText}>Order #{item.order_id}</Text>
              <Text style={s.resultErrorText}>{item.error}</Text>
            </View>
          ))}
        </ScrollView>

        {/* Re-run failed orders button */}
        {failed.length > 0 && onRerunFailed && (
          <TouchableOpacity onPress={handleRerunFailed} style={s.rerunFailedButton}>
            <Text style={s.rerunFailedText}>Re-run failed orders</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderPolling = () =>
    renderLoading(`Processing ${orderIds.length} orders...`);

  const renderSkippedPreview = () => (
    <View style={s.loadingContainer}>
      <MaterialIcons name="info-outline" size={32} color="#f39c12" />
      <Text style={[s.loadingText, { textAlign: 'center', marginTop: 12, paddingHorizontal: 16 }]}>
        Preview is not available for more than {DRY_RUN_THRESHOLD} orders.
      </Text>
      <Text style={[s.loadingText, { fontSize: 12, color: '#9aafc0', textAlign: 'center', marginTop: 6 }]}>
        Click "Apply Changes" to process all {orderIds.length} orders directly.
      </Text>
    </View>
  );

  // ─── Modal content router ──────────────────────────────────────────────────

  const renderContent = () => {
    if (loadingPreview) return renderLoading('Loading preview...');
    if (error) return renderError();
    if (polling) return renderPolling();
    if (applying) return renderLoading('Applying changes...');
    if (applyResult) return renderApplyResult();
    if (dryRunSkipped) return renderSkippedPreview();
    if (previewData) return renderPreview();
    return renderLoading('Loading...');
  };

  // ─── Title ──────────────────────────────────────────────────────────────────

  const getTitle = () => {
    if (applyResult) return 'Apply Results';
    if (polling) return `Processing ${orderIds.length} orders...`;
    if (dryRunSkipped) return `Apply Rules -- ${orderIds.length} orders`;
    return `Rule Evaluation Preview -- ${orderIds.length} orders`;
  };

  // ─── Should show Apply button ───────────────────────────────────────────────

  const showApplyButton =
    (previewData || dryRunSkipped) && !error && !polling && !applying && !applyResult;

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (!visible) return null;

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={[s.dialog, { width: windowWidth >= 900 ? '60%' : '95%' }]}>
          {/* Header */}
          <View style={s.header}>
            <Text style={s.headerTitle}>{getTitle()}</Text>
            <TouchableOpacity onPress={onClose} style={s.closeIcon}>
              <AntDesign name="close" size={20} color="#E0E7EF" />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <View style={s.body}>{renderContent()}</View>

          {/* Footer */}
          <View style={s.footer}>
            <TouchableOpacity onPress={onClose} style={s.cancelButton}>
              <Text style={s.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            {showApplyButton && (
              <TouchableOpacity onPress={handleApply} style={s.applyButton}>
                <Text style={s.applyButtonText}>Apply Changes</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

ReRunRulesModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  tenantId: PropTypes.string.isRequired,
  onRerunFailed: PropTypes.func,
  alertBox: PropTypes.func,
  skipDryRun: PropTypes.bool,
};

export default ReRunRulesModal;
